import {
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import React, { useEffect, useState } from 'react';
import {
  openViewDatasourceListAtom,
  viewDatasourcesUsedInfoAtom,
} from './HeaderState';
import { Dialog, DialogContent, DialogTitle, Box, Theme } from '@mui/material';
import { usePlatform } from 'platform/PlatformHooks';
import { AnyItem, getManager } from 'componentregistry';
import { DataStoreItem } from 'componentregistry/components/DataStore/DataStore';
import Icon from 'icons/Icon';
import { Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import {
  activeSidebarPanelAtom,
  sidebarDataSourceFilterAtom,
  sidebarFunctionsFilterAtom,
} from 'layout/Sidebar/atoms';
import useDataStore from 'store/useDataStore';
import { IoDatasourceAccess, IoPages } from '@cloudio-saas/datasource-types';
import WaveDots from 'utils/WaveDots';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Chip } from '@mui/material';
import { TypographyEllipsisProps } from 'utils/commonProps';

const useStyles = makeStyles((theme: Theme) => ({
  box: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

export const isOfTypeDataStore = (type: string) =>
  [
    'DataStore',
    'ReadOnlyDataStore',
    'TransientDataStore',
    'CloudDataStore',
    'LocalDataStore',
  ].includes(type);

function DatasourceList() {
  const platform = usePlatform();
  const { appUid } = platform;
  const viewDatasourcesUsedInfo = useStateXAtomValue(
    viewDatasourcesUsedInfoAtom,
  );
  const { pageId } = viewDatasourcesUsedInfo;
  const pName = useStateXValue(
    ['db', 'IoPages', platform.pageId, 'pageName'],
    '',
  );
  const pageName = viewDatasourcesUsedInfo.pageName ?? pName;
  const pageRefStore = useDataStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesDSUsedAlias',
  });
  const dsAccessStore = useDataStore<IoDatasourceAccess>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceAccess',
    alias: 'IoDatasourceAccessDSUsedAlias',
  });
  const [datasources, setDatasources] = useState<
    Record<string, { dsAlias: string; item: AnyItem; roles: string[] }>
  >({});
  const [fetched, setFetched] = useState<boolean>(false);
  const classes = useStyles();

  useEffect(() => {
    async function query() {
      await pageRefStore.query({
        props: {
          projection: {
            pageName: 1,
            md: 1,
          },
          filter: [{ appUid: { is: appUid } }, { pageUid: { is: pageId } }],
        },
      });
      const record = pageRefStore.getCurrentRecord();
      if (record) {
        const { md } = record;
        if (md) {
          const ids = md ? Object.keys(md) : [];
          const dsList: Record<
            string,
            { dsAlias: string; item: AnyItem; roles: string[] }
          > = {};
          ids.forEach((id) => {
            const item = md[id] as AnyItem;
            const mgr = getManager(item.type);
            if (isOfTypeDataStore(mgr.type)) {
              const dsItem = item as DataStoreItem;
              dsList[dsItem.props.dataSource] = {
                dsAlias: dsItem.props.alias,
                item: dsItem,
                roles: [],
              };
            }
          });
          if (Object.keys(dsList).length) {
            await dsAccessStore.query({
              props: {
                projection: {
                  roleUid: 1,
                  datasourceUid: 1,
                },
                filter: [
                  { appUid: { is: appUid } },
                  { datasourceUid: { in: Object.keys(dsList) } },
                ],
              },
            });
            const records = dsAccessStore.records();
            Object.keys(dsList).forEach((key) => {
              const roles: string[] = [];
              Object.values(records).forEach((r) => {
                if (r.datasourceUid === key && r.roleUid) {
                  roles.push(r.roleUid);
                }
              });
              dsList[key]['roles'] = roles;
            });
          }
          setDatasources(dsList);
        }
      }
      setFetched(true);
    }
    query();
  }, [pageRefStore, pageId, appUid, dsAccessStore]);
  const setOpenDatasourceList = useStateXAtomSetter(openViewDatasourceListAtom);
  const set = useStateXSetter();
  const onClose = () => setOpenDatasourceList(false);

  const keys = Object.keys(datasources);
  const handleCloudDS = (ds: string) => {
    set(activeSidebarPanelAtom, 'functions');
    set(sidebarFunctionsFilterAtom, ds);
  };

  const handleLocalDS = (itemId: string) => {
    platform.selectItemInDesigner({
      appUid,
      pageId,
      itemId,
    });
  };

  const onDsClick = (ds: string) => {
    onClose();
    const { item } = datasources[ds];
    const { type } = item;
    if (type === 'CloudDataStore') {
      handleCloudDS(ds);
      return;
    }
    if (type === 'LocalDataStore') {
      handleLocalDS(item.itemId);
      return;
    }
    set(activeSidebarPanelAtom, 'ds');
    set(sidebarDataSourceFilterAtom, `ds:${ds}`);
  };

  const content = !keys.length ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: '24px',
      }}
    >
      <Typography variant="subtitle1">No Datasources Found!</Typography>
    </Box>
  ) : (
    keys.map((ds) => {
      const { item, roles } = datasources[ds];
      const { type } = item;
      return (
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            cursor: 'pointer',
            justifyContent: 'space-between',
            p: '4px 24px',
            maxHeight: '76px',
          }}
          className={clsx(classes.box)}
          onClick={() => onDsClick(ds)}
          data-tip={`Go To Datasource ${ds}`}
        >
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Box
              sx={{
                alignSelf: roles.length ? 'flex-start' : undefined,
                mt: roles.length ? '6px' : undefined,
              }}
            >
              <Icon iconName="database" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                sx={{ ...TypographyEllipsisProps }}
              >
                {ds}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  maxWidth: '400px',
                  '-webkit-line-clamp': '3',
                  '-webkit-box-orient': 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {roles.join(', ')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Chip
              label={type}
              size="small"
              color="primary"
              sx={{ height: '20px' }}
            />
          </Box>
        </Box>
      );
    })
  );

  return (
    <Dialog open={true} sx={{ maxHeight: '700px' }}>
      <DialogTitle sx={{ m: 0, p: 2, pr: '72px', width: '450px' }}>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ ...TypographyEllipsisProps }}
            fontWeight={600}
          >
            {`Referred Datasources - ${pageName}`}
          </Typography>
          {fetched && keys.length > 0 ? (
            <Chip label={keys.length} size="small" />
          ) : null}
        </Box>
        <IconButton
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', width: '520px', pl: 0, pr: 0 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {fetched ? content : <WaveDots active />}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export function DSListDialog() {
  const openDsList = useStateXAtomValue(openViewDatasourceListAtom);
  return openDsList ? <DatasourceList /> : null;
}
